// ============================================================
// EDIT ESTABLISHMENT FORM - With Automatic Re-Geocoding
// File: src/components/EditEstablishmentForm.js
// Edits existing establishments and re-geocodes if address changes
// ============================================================

import React, { useState, useEffect } from 'react'
import toast from 'react-hot-toast'
import { supabase } from '../supabaseClient'
import { geocodeAddress } from '../utils/geocoding'
import { logAuditEvent, AUDIT_ACTIONS } from '../utils/auditLog'
import { Input, Select, Textarea, Button, Card } from './UI'
import { useTheme } from '../ThemeContext'
import { useAgents } from '../hooks/useAgents'
import { useOrganization } from '../hooks/useOrganization'

export default function EditEstablishmentForm({ establishment, onClose, onSuccess }) {
  const { colors, spacing, typography, radius, brand, darkMode } = useTheme()
  const { organization } = useOrganization()
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  const idLabel = organization?.settings?.identifier_label || 'MXB Number'
  const { agents } = useAgents({ activeOnly: true, role: ['inspector', 'admin'] })
  const [formData, setFormData] = useState({
    mxb_number: establishment.mxb_number || '',
    trade_name: establishment.trade_name || '',
    address: establishment.address || '',
    city: establishment.city || '',
    state: establishment.state || 'NC',
    zip: establishment.zip || '',
    assigned_inspector_id: establishment.assigned_inspector_id || '',
    notes: establishment.notes || ''
  })

  const [originalAddress, setOriginalAddress] = useState('')
  const [loading, setLoading] = useState(false)
  const [isGeocoding, setIsGeocoding] = useState(false)
  const [error, setError] = useState(null)
  const [geocodeWarning, setGeocodeWarning] = useState(null)
  const [addressChanged, setAddressChanged] = useState(false)

  // Store original address for comparison
  useEffect(() => {
    const original = `${establishment.address}|${establishment.city}|${establishment.state}|${establishment.zip}`
    setOriginalAddress(original)
  }, [establishment])

  // Check if address changed
  useEffect(() => {
    const current = `${formData.address}|${formData.city}|${formData.state}|${formData.zip}`
    setAddressChanged(current !== originalAddress)
  }, [formData.address, formData.city, formData.state, formData.zip, originalAddress])

  function handleChange(e) {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    setError(null)
    setGeocodeWarning(null)
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setGeocodeWarning(null)

    try {
      // Validate required fields
      if (!formData.mxb_number || !formData.trade_name || !formData.address || 
          !formData.city || !formData.state || !formData.zip) {
        throw new Error('Please fill in all required fields')
      }

      let updateData = {
        mxb_number: formData.mxb_number.trim(),
        trade_name: formData.trade_name.trim(),
        address: formData.address.trim(),
        city: formData.city.trim(),
        state: formData.state,
        zip: formData.zip.trim(),
        assigned_inspector_id: formData.assigned_inspector_id || null,
        notes: formData.notes.trim()
      }

      // If address changed, re-geocode
      if (addressChanged) {
        setIsGeocoding(true)
        console.log('Address changed, re-geocoding...')
        
        const geocodeResult = await geocodeAddress(
          formData.address,
          formData.city,
          formData.state,
          formData.zip
        )

        if (geocodeResult.success) {
          updateData.latitude = geocodeResult.latitude
          updateData.longitude = geocodeResult.longitude
          console.log('✅ Re-geocoded successfully:', updateData.latitude, updateData.longitude)
        } else {
          console.warn('⚠️ Re-geocoding failed:', geocodeResult.error)
          setGeocodeWarning(geocodeResult.error)
          // Set coordinates to null if geocoding fails
          updateData.latitude = null
          updateData.longitude = null
        }
        
        setIsGeocoding(false)
      }

      // Update establishment
      const { data, error: updateError } = await supabase
        .from('establishments')
        .update(updateData)
        .eq('id', establishment.id)
        .select()

      if (updateError) throw updateError

      console.log('✅ Establishment updated:', data[0])

      await logAuditEvent(AUDIT_ACTIONS.ESTABLISHMENT_UPDATED, {
        establishment_id: establishment.id,
        mxb_number: establishment.mxb_number,
        trade_name: data[0].trade_name,
        address_changed: addressChanged
      })

      // Success message
      if (addressChanged && updateData.latitude && updateData.longitude) {
        toast.success('Establishment updated and location refreshed on map!')
      } else if (addressChanged) {
        toast.success('Establishment updated! Note: Location could not be found on map.', { duration: 5000 })
      } else {
        toast.success('Establishment updated successfully!')
      }

      onSuccess()
    } catch (err) {
      console.error('Error updating establishment:', err)
      setError(err.message)
    } finally {
      setLoading(false)
      setIsGeocoding(false)
    }
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: isMobile ? 'flex-start' : 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? 0 : spacing.md
    }}>
      <Card style={{
        maxWidth: isMobile ? '100%' : '600px',
        width: '100%',
        height: isMobile ? '100dvh' : 'auto',
        maxHeight: isMobile ? '100dvh' : '90vh',
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        borderRadius: isMobile ? 0 : undefined
      }} padding="lg">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: spacing.xl
        }}>
          <h2 style={{
            margin: 0,
            fontSize: typography.fontSizes['2xl'],
            fontWeight: typography.fontWeights.semibold,
            color: colors.text.primary
          }}>
            Edit Establishment
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: typography.fontSizes['3xl'],
              cursor: 'pointer',
              color: colors.text.tertiary,
              lineHeight: 1
            }}
          >
            ×
          </button>
        </div>

        {error && (
          <div style={{
            padding: spacing.md,
            backgroundColor: darkMode ? 'rgba(153,27,27,0.2)' : '#fee2e2',
            border: `1px solid ${brand.danger}`,
            borderRadius: radius.md,
            color: darkMode ? '#fca5a5' : brand.danger,
            marginBottom: spacing.lg,
            fontSize: typography.fontSizes.sm
          }}>
            {error}
          </div>
        )}

        {addressChanged && (
          <div style={{
            padding: spacing.md,
            backgroundColor: darkMode ? 'rgba(30,64,175,0.2)' : '#dbeafe',
            border: `1px solid ${brand.info}`,
            borderRadius: radius.md,
            color: darkMode ? '#93c5fd' : '#1e40af',
            marginBottom: spacing.lg,
            fontSize: typography.fontSizes.sm
          }}>
            📍 Address changed - location will be updated on map
          </div>
        )}

        {geocodeWarning && (
          <div style={{
            padding: spacing.md,
            backgroundColor: darkMode ? 'rgba(146,64,14,0.2)' : '#fef3c7',
            border: `1px solid ${brand.warning}`,
            borderRadius: radius.md,
            color: darkMode ? '#fcd34d' : '#92400e',
            marginBottom: spacing.lg,
            fontSize: typography.fontSizes.sm
          }}>
            ⚠️ {geocodeWarning}
            <br />
            <small>Establishment will be updated but may not appear correctly on the map.</small>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: spacing.lg }}>
            <Input
              label={idLabel}
              name="mxb_number"
              value={formData.mxb_number}
              onChange={handleChange}
              required
            />
          </div>

          <div style={{ marginBottom: spacing.lg }}>
            <Input
              label="Trade Name"
              name="trade_name"
              value={formData.trade_name}
              onChange={handleChange}
              required
            />
          </div>

          <div style={{ marginBottom: spacing.lg }}>
            <Input
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              required
              helperText={addressChanged ? '📍 Location will be updated automatically' : 'Current location will be preserved'}
            />
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr',
            gap: spacing.sm,
            marginBottom: spacing.lg
          }}>
            <Input
              label="City"
              name="city"
              value={formData.city}
              onChange={handleChange}
              required
            />

            <Select
              label="State"
              name="state"
              value={formData.state}
              onChange={handleChange}
              required
              options={[
                { label: 'NC', value: 'NC' },
                { label: 'SC', value: 'SC' },
                { label: 'VA', value: 'VA' },
                { label: 'GA', value: 'GA' }
              ]}
            />

            <Input
              label="Zip"
              name="zip"
              value={formData.zip}
              onChange={handleChange}
              required
              maxLength="5"
              pattern="[0-9]{5}"
            />
          </div>

          <div style={{ marginBottom: spacing.lg }}>
            <Select
              label="Assigned Agent (Optional)"
              name="assigned_inspector_id"
              value={formData.assigned_inspector_id}
              onChange={handleChange}
              options={[
                { label: 'Unassigned', value: '' },
                ...agents.map(agent => ({
                  label: `${agent.first_name} ${agent.last_name}`,
                  value: agent.id
                }))
              ]}
            />
          </div>

          <div style={{ marginBottom: spacing.xl }}>
            <Textarea
              label="Notes (Optional)"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={4}
              placeholder="Add any notes about this establishment..."
            />
          </div>

          <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'flex-end' }}>
            <Button
              type="button"
              onClick={onClose}
              disabled={loading || isGeocoding}
              variant="secondary"
              size="lg"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || isGeocoding}
              loading={loading || isGeocoding}
              variant="primary"
              size="lg"
            >
              {isGeocoding ? '📍 Updating location...' : 'Update Establishment'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
